import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import DateBar, { today, daysAgo } from "../components/DateBar";
import TopProductsChart from "../components/TopProductsChart";
import StateTable from "../components/StateTable";
import TopCustomers from "../components/TopCustomers";
import ProductTable from "../components/ProductTable";
import ProductUnsoldList from "../components/ProductUnsoldList";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

const fmt = (v) =>
  "₹" + Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 });

function SalesKPIRow({ kpi, topState, bottomState }) {
  const fmt = (v) =>
    "₹" + Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const truncate = (str, n = 12) =>
    str ? (str.length > n ? str.slice(0, n) + "…" : str) : "N/A";

  const items = [
    {
      label: "Basic Revenue",
      value: fmt(kpi?.total_revenue ?? 0),
      color: "var(--green)",
      sub: "Excluding tax",
    },
    {
      label: "Total Bills",
      value: (kpi?.total_bills ?? 0).toLocaleString("en-IN"),
      color: "var(--blue)",
    },
    {
      label: "Unique Customers",
      value: (kpi?.unique_customers ?? 0).toLocaleString("en-IN"),
      color: "var(--purple)",
    },
    {
      label: "Total Qty Sold",
      value: (kpi?.total_qty ?? 0).toLocaleString("en-IN"),
      color: "var(--amber)",
    },
    { label: "Total CGST", value: fmt(kpi?.total_cgst ?? 0), color: "var(--teal)" },
    { label: "Total SGST", value: fmt(kpi?.total_sgst ?? 0), color: "var(--violet)" },
    { label: "Total IGST", value: fmt(kpi?.total_igst ?? 0), color: "var(--orange)" },
    { label: "Total Tax", value: fmt(kpi?.total_tax ?? 0), color: "var(--red)" },
    {
      label: "Total Revenue",
      value: fmt(kpi?.total_revenue_incl ?? 0),
      color: "var(--emerald)",
      sub: "Including tax",
    },
    {
      label: "Total Discount",
      value: fmt(kpi?.total_discount ?? 0),
      color: "var(--orange2)",
    },
    {
      label: "Avg Bill Value",
      value: fmt(kpi?.avg_bill_value ?? 0),
      color: "var(--green-dark)",
    },
  ];

  return (
    <div className="summary-row">
      {/* Regular KPI cards */}
      {items.map((c) => (
        <div
          key={c.label}
          className="summary-card"
          style={{ borderTopColor: c.color }}
        >
          <div className="summary-label">{c.label}</div>

          <div className="summary-value">
            {kpi ? c.value : <span className="skeleton" />}
          </div>
          <div
            style={{
              fontSize: "11px",
              color: "var(--muted)",
              marginTop: "4px",
            }}
          >
            {c.sub && (
              <span
                style={{
                  marginLeft: "4px",
                  fontSize: "10px",
                  color: "var(--light)",
                  fontWeight: 400,
                }}
              >
                ({c.sub})
              </span>
            )}
          </div>
        </div>
      ))}

      {/* 🏆 Best Selling State */}
      <div className="summary-card" style={{ borderTopColor: "var(--pink)" }}>
        <div className="summary-label">
          <span style={{ marginRight: "4px" }}>🏆</span>Best Selling State
        </div>
        {topState ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  background: "var(--green-light)",
                  flexShrink: 0,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M6 10V2M6 2L2 6M6 2L10 6"
                    stroke="var(--green-dark)"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <div
                className="summary-value"
                style={{ fontSize: "15px", color: "var(--pink)" }}
              >
                {truncate(topState.state)}
              </div>
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "var(--muted)",
                marginTop: "4px",
              }}
            >
              {fmt(topState.revenue)} · {topState.total_bills} bills
            </div>
          </>
        ) : (
          <span className="skeleton" />
        )}
      </div>

      {/* 📉 Lowest Selling State */}
      <div className="summary-card" style={{ borderTopColor: "var(--crimson)" }}>
        <div className="summary-label">
          <span style={{ marginRight: "4px" }}>📉</span>Lowest Selling State
        </div>
        {bottomState ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  background: "var(--red-light)",
                  flexShrink: 0,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M6 2V10M6 10L2 6M6 10L10 6"
                    stroke="var(--crimson)"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <div
                className="summary-value"
                style={{ fontSize: "15px", color: "var(--crimson)" }}
              >
                {truncate(bottomState.state)}
              </div>
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "var(--muted)",
                marginTop: "4px",
              }}
            >
              {fmt(bottomState.revenue)} · {bottomState.total_bills} bills
            </div>
          </>
        ) : (
          <span className="skeleton" />
        )}
      </div>
    </div>
  );
}

export default function SalesReport({ showToast }) {
  const [preset, setPreset] = useState(1);
  const [start, setStart] = useState(daysAgo(6));
  const [end, setEnd] = useState(today);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("Daily");
  const [kpi, setKpi] = useState(null);
  const [daily, setDaily] = useState([]);
  const [products, setProducts] = useState([]);
  const [states, setStates] = useState([]);
  const [customers, setCustomers] = useState([]);

  const TABS = [
    { key: "Daily", icon: "📅" },
    { key: "Products", icon: "🏆" },
    { key: "States", icon: "🗺️" },
    { key: "Customers", icon: "👥" },
  ];

  const fetchAll = useCallback(
    async (s, e) => {
      setLoading(true);
      try {
        const [k, d, p, st, c] = await Promise.all([
          api.kpi(s, e),
          api.daily(s, e),
          api.products(s, e),
          api.states(s, e),
          api.customers(s, e),
        ]);
        setKpi(k);
        setDaily(d);
        setProducts(p);
        setStates(st);
        setCustomers(c);
      } catch {
        showToast("Failed to fetch sales data", "error");
      } finally {
        setLoading(false);
      }
    },
    [showToast],
  );

  useEffect(() => {
    fetchAll(start, end);
  }, [start, end, fetchAll]);

  const totalBills = daily.reduce((s, d) => s + d.total_bills, 0);
  const totalRevenue = daily.reduce((s, d) => s + d.total_revenue, 0);
  const totalQty = daily.reduce((s, d) => s + d.total_qty, 0);
  const totalTax = daily.reduce((s, d) => s + d.total_tax, 0);
  const topState = states.length > 0 ? states[0] : null;
  const bottomState = states.length > 0 ? states[states.length - 1] : null;

  return (
    <div className="page-body">
      <div className="page-title">Sales Report</div>
      <div className="page-subtitle">
        Detailed sales performance — bills, revenue, products and customers
      </div>

      <DateBar
        start={start}
        end={end}
        preset={preset}
        setStart={setStart}
        setEnd={setEnd}
        setPreset={setPreset}
        loading={loading}
      />
      <SalesKPIRow kpi={kpi} topState={topState} bottomState={bottomState} />

      {/* Tabs */}
      <div className="tabs-wrap">
        <div className="tabs-inner">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`tab-btn ${tab === t.key ? "active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.icon} {t.key}
            </button>
          ))}
        </div>
      </div>

      {/* ── Daily tab ─────────────────────────────── */}
      {tab === "Daily" && (
        <>
          {/* Revenue bar chart */}
          <div className="card">
            <div className="card-title">Daily revenue & quantity sold</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart
                data={daily}
                margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--grid)" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => d.slice(5).replace("-", "/")}
                  tick={{ fontSize: 10 }}
                />
                <YAxis
                  yAxisId="rev"
                  tickFormatter={(v) => "₹" + (v / 1000).toFixed(0) + "k"}
                  tick={{ fontSize: 10 }}
                  width={52}
                />
                <YAxis
                  yAxisId="qty"
                  orientation="right"
                  tick={{ fontSize: 10 }}
                  width={35}
                />
                <Tooltip
                  formatter={(v, name) => [
                    name === "Revenue" ? fmt(v) : v,
                    name,
                  ]}
                />
                <Legend />
                <Bar
                  yAxisId="rev"
                  dataKey="total_revenue"
                  fill="var(--green)"
                  name="Revenue"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  yAxisId="qty"
                  dataKey="total_qty"
                  fill="var(--blue)"
                  name="Qty"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Bills & customers line chart */}
          <div className="card">
            <div className="card-title">Daily bills & unique customers</div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart
                data={daily}
                margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--grid)" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => d.slice(5).replace("-", "/")}
                  tick={{ fontSize: 10 }}
                />
                <YAxis tick={{ fontSize: 10 }} width={35} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="total_bills"
                  stroke="var(--purple)"
                  strokeWidth={2}
                  dot={false}
                  name="Bills"
                />
                <Line
                  type="monotone"
                  dataKey="unique_customers"
                  stroke="var(--amber)"
                  strokeWidth={2}
                  dot={false}
                  name="Customers"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Daily breakdown table */}
          <div className="card">
            <div className="card-title">Day-by-day breakdown</div>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th style={{ textAlign: "center" }}>Bills</th>
                    <th style={{ textAlign: "right" }}>Revenue</th>
                    <th style={{ textAlign: "center" }}>Qty</th>
                    <th style={{ textAlign: "right" }}>Tax</th>
                    <th style={{ textAlign: "center" }}>Customers</th>
                    <th style={{ textAlign: "right" }}>Avg Bill</th>
                  </tr>
                </thead>
                <tbody>
                  {daily.map((d) => (
                    <tr key={d.date}>
                      <td style={{ fontWeight: 500, whiteSpace: "nowrap" }}>
                        {d.date}
                      </td>
                      <td style={{ textAlign: "center" }}>{d.total_bills}</td>
                      <td
                        style={{
                          textAlign: "right",
                          color: "var(--green)",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fmt(d.total_revenue)}
                      </td>
                      <td style={{ textAlign: "center" }}>{d.total_qty}</td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {fmt(d.total_tax)}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {d.unique_customers}
                      </td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {fmt(d.avg_bill_value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td style={{ textAlign: "center" }}>{totalBills}</td>
                    <td
                      style={{
                        textAlign: "right",
                        color: "var(--green)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmt(totalRevenue)}
                    </td>
                    <td style={{ textAlign: "center" }}>{totalQty}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {fmt(totalTax)}
                    </td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === "Products" && (
        <>
          <TopProductsChart data={products} start={start} end={end} />
          <ProductUnsoldList products={products} start={start} end={end} />
          {/* <div className="card">
            <div className="card-title">All products</div>
            <div className="table-scroll">
              <ProductTable products={products} start={start} end={end} />
            </div>
          </div> */}
        </>
      )}

      {tab === "States" && <StateTable data={states} start={start} end={end} />}
      {tab === "Customers" && (
        <TopCustomers data={customers} start={start} end={end} />
      )}
    </div>
  );
}
